import React, { useRef, useState, useEffect } from 'react';
import { BarChart3, Activity, TrendingUp, GitCompare, PieChart, LineChart, ArrowRight, Heart, GraduationCap, Home, AlertTriangle } from 'lucide-react';
import { calculators } from '../data/mockData';
import { Link } from 'react-router-dom';

const iconMap = {
  BarChart3,
  Activity,
  TrendingUp,
  GitCompare,
  PieChart,
  LineChart,
  Heart,
  GraduationCap,
  Home,
  AlertTriangle
};

const CalculatorsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-32 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className={`flex flex-col lg:flex-row lg:items-end justify-between mb-16 transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div>
            <span className="text-white/40 text-sm uppercase tracking-widest mb-4 block">PLAN YOUR WEALTH</span>
            <h2 className="text-4xl lg:text-5xl font-bold text-white" data-testid="calculators-section-heading">
              Smart Financial
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
                Calculators
              </span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md mt-6 lg:mt-0">
            Our core aim is to serve our clients with dedication and integrity,
            meet their expectations, and build enduring relationships.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {calculators.map((calc, index) => {
            const IconComponent = iconMap[calc.icon];
            return (
              <Link
                key={calc.id}
                to="/calculators"
                className={`group relative p-8 bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 hover:border-white/20 transition-all duration-500 hover:bg-white/10 hover:-translate-y-2 transform ${
                  isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="p-4 bg-gradient-to-br from-white/10 to-white/5 rounded-2xl group-hover:scale-110 transition-transform duration-500">
                    {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                  </div>
                  <ArrowRight className="w-5 h-5 text-white/30 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                </div>

                <h3 className="text-xl font-bold text-white mb-3">
                  {calc.title}
                </h3>

                <p className="text-white/50 text-sm leading-relaxed">
                  {calc.description}
                </p>

                {/* Hover Glow */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-500/0 to-cyan-500/0 group-hover:from-emerald-500/10 group-hover:to-cyan-500/10 transition-all duration-500" />
              </Link>
            );
          })}
        </div>

        {/* Featured Calculator Card */}
        <div className={`mt-16 grid lg:grid-cols-3 gap-8 transform transition-all duration-1000 delay-500 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="relative p-10 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-3xl border border-white/10 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <span className="inline-block px-4 py-1 bg-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
                Featured
              </span>
              <h3 className="text-3xl font-bold text-white mb-4">Crorepati Calculator</h3>
              <p className="text-white/70 mb-8">
                Plan your path to ₹1 crore by estimating investment amounts, returns, and timelines.
                Get a clear roadmap for long-term wealth creation.
              </p>
              <Link
                to="/calculators/crorepati"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full hover:shadow-lg transition-all duration-300 group"
              >
                Calculate Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>

          <div className="relative p-10 bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-3xl border border-white/10 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <span className="inline-block px-4 py-1 bg-violet-500/20 rounded-full text-violet-400 text-sm font-medium mb-6">
                Advanced
              </span>
              <h3 className="text-3xl font-bold text-white mb-4">Portfolio Forecasting</h3>
              <p className="text-white/70 mb-8">
                AI-powered portfolio analysis with multi-scenario forecasting, risk assessment, and inflation-adjusted projections.
              </p>
              <Link
                to="/calculators/portfolio-forecast"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full hover:shadow-lg transition-all duration-300 group"
              >
                Forecast Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>

          <div className="relative p-10 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-3xl border border-white/10 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <span className="inline-block px-4 py-1 bg-purple-500/20 rounded-full text-purple-400 text-sm font-medium mb-6">
                Popular
              </span>
              <h3 className="text-3xl font-bold text-white mb-4">SIP vs EMI</h3>
              <p className="text-white/70 mb-8">
                Compare the power of systematic investing against loan EMIs.
                Understand how small investments can outpace debt payments.
              </p>
              <Link
                to="/calculators/sip-emi"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-semibold rounded-full hover:shadow-lg transition-all duration-300 group"
              >
                Compare Now
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CalculatorsSection;
